import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import { Menu, MenuItem } from '../components/Menu';
import CategoryTable from '../components/expenses/categories/CategoryTable';
import CategoryFormDrawer from '../components/expenses/categories/CategoryFormDrawer';
import useCategoryList, { SORTS } from '../hooks/useCategoryList';
import { useExpenses, BUILT_IN_NOT_SEEDED } from '../data/useExpenses';
import { expenseCategoryErrors } from '../domain/expenses';
import { categoryDeleteMessage } from '../domain/expenseForms';
import s from '../components/expenses/categories/categories.module.css';

// Expenses & Other Revenue → Categories.
//
// Reached from the Actions menu on the expenses screen, and a real page rather
// than a drawer because MMS makes it one — it has its own title, its own sort
// and its own pagination.

export default function ExpenseCategoriesPage() {
  const {
    categoryRecords, categoryUsage, categoriesSeeded, categoriesRead, seedCategories,
    addCategory, renameCategory, removeCategory,
  } = useExpenses();

  // A sentence about the built-ins not being editable yet, or about a delete
  // refused — shown under the bar.
  const [notice, setNotice] = useState(null);

  // The built-ins become documents the first time this page is opened, the
  // first place they can be edited, so a studio that never opens the manager
  // never carries the rows.
  //
  // Never on this device's word alone (#631): before the read answers, "not
  // seeded" is only "not read", and a cold cache can answer empty too, so a
  // seed then would set the built-ins back over a studio's renamed and deleted
  // ones. `seedExpenseCategories` in data/useExpenses.jsx asks the server
  // whether the seed is there and writes nothing when it cannot; the window
  // answering only spares a seeded studio the round trip. Once per visit: a
  // refusal is not retried on every render.
  //
  // A server that could not be reached is not a refusal (#681): the seed
  // resolves having written nothing, no notice is shown, and the next visit
  // asks again. Any other failure of the read rejects, and says so here.
  const asked = useRef(false);
  useEffect(() => {
    if (!categoriesRead || categoriesSeeded || asked.current) return;
    asked.current = true;
    seedCategories().then(undefined, (err) => {
      console.warn('[expense categories] the read that asks whether the built-in categories are saved '
        + 'failed for a reason other than the connection, so they were not; the next visit asks again:', err);
      setNotice(BUILT_IN_NOT_SEEDED);
    });
  }, [categoriesRead, categoriesSeeded, seedCategories]);

  const list = useCategoryList({ categoryRecords, categoryUsage });

  const [sortOpen, setSortOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | { id, type, from }
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('expense');
  const [confirm, setConfirm] = useState(null);
  const [nameError, setNameError] = useState(null);

  const closeForm = () => { setEditing(null); setName(''); setNameError(null); };

  // Typing clears the refusal, as `patch` in useExpenseEntryForm and
  // `surviving` in CategoriesDrawer do: the message is about the name that was
  // submitted.
  const changeName = (value) => { setName(value); setNameError(null); };

  // Add and Rename are refused on the same rule (#144). Rename especially: it
  // rewrites every entry filed under the old label, so renaming into a name the
  // studio already has merges two categories' entries with nothing said. See
  // `expenseCategoryErrors`.
  const saveForm = async () => {
    if (!name.trim()) return;
    const draft = editing === 'new'
      ? { id: null, type: newType, label: name }
      : { id: editing.id, type: editing.type, label: name };
    const refusals = expenseCategoryErrors(draft, categoryRecords());
    if (refusals.label) { setNameError(refusals.label); return; }
    if (editing === 'new') await addCategory({ type: newType, label: name });
    else {
      // A refusal is a sentence (`BUILT_IN_NOT_SEEDED`); nothing was written.
      const refused = await renameCategory({ id: editing.id, type: editing.type, from: editing.from, to: name });
      if (typeof refused === 'string') { setNameError(refused); return; }
    }
    closeForm();
  };

  return (
    <AppShell title="Expenses & Other Revenue Categories">
      <div className={s.page}>
        <Link to="/expenses" className={s.back}>‹ Back to Expenses &amp; Revenue</Link>

        <div className={s.bar}>
          <Button onClick={() => { setNewType('expense'); setName(''); setNameError(null); setEditing('new'); }}>
            + New Category
          </Button>
          <span className={s.spacer} />
          <Menu
            open={sortOpen}
            onClose={() => setSortOpen(false)}
            trigger={<Button variant="secondary" onClick={() => setSortOpen((v) => !v)}>Sort ▾</Button>}
          >
            {SORTS.map((option) => (
              <MenuItem
                key={option.key}
                onClick={() => { list.chooseSort(option.key); setSortOpen(false); }}
              >
                {option.label}
                {list.sort.key === option.key ? (list.sort.desc ? ' ↑' : ' ↓') : ''}
              </MenuItem>
            ))}
          </Menu>
        </div>

        {notice && <p className={s.notice} role="status">{notice}</p>}

        <CategoryTable
          rows={list.shown}
          total={list.rows.length}
          sort={list.sort}
          openMenuId={rowMenu}
          onToggleMenu={setRowMenu}
          onEdit={(c) => {
            setEditing({ id: c.id, type: c.type, from: c.label });
            setName(c.label);
            setNameError(null);
            setRowMenu(null);
          }}
          onDelete={(c) => { setConfirm(c); setRowMenu(null); }}
          page={list.page}
          pageCount={list.pageCount}
          perPage={list.perPage}
          onPageChange={list.setPage}
          onPerPageChange={list.setPerPage}
        />
      </div>

      <CategoryFormDrawer
        editing={editing}
        name={name}
        onNameChange={changeName}
        error={nameError}
        type={newType}
        onTypeChange={setNewType}
        onSave={saveForm}
        onClose={closeForm}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete Category"
        message={categoryDeleteMessage(confirm?.label, confirm?.count)}
        confirmLabel="Delete"
        discardLabel="Cancel"
        cancelLabel=""
        onDiscard={() => setConfirm(null)}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const refused = await removeCategory({
            id: confirm.id, type: confirm.type, label: confirm.label, isSystem: confirm.isSystem,
          });
          setNotice(typeof refused === 'string' ? refused : null);
          setConfirm(null);
        }}
      />
    </AppShell>
  );
}
